import React, { useMemo, useState, useEffect } from 'react';
import { DollarSign, FileText, CheckCircle, Clock, AlertCircle, Download, Calendar, Search } from 'lucide-react';
import { Pagination } from '@mui/material';

const QuotasConfirmed = ({ quotas, ufUsed, onExportExcel }) => {
  const [page, setPage] = useState(1);
  
  // Estados para búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('none'); // none, amount_asc, amount_desc, company_asc, company_desc, status_asc, status_desc
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, paid, overdue
  
  const itemsPerPage = 10;

  // Filtrar y ordenar las cuotas
  const filteredAndSortedQuotas = useMemo(() => {
    let filtered = quotas;

    // Filtrar por término de búsqueda
      if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = quotas.filter(quota => {
        const name = (quota.company.trade_name || quota.company.business_name || '').toString().toLowerCase();
        return (
          name.includes(term) ||
          (quota.company.rut || '').toString().toLowerCase().includes(term) ||
          quota.id.toString().includes(term)
        );
      });
    }

    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(quota => quota.status === statusFilter);
    }

    // Ordenar según el criterio seleccionado
    if (sortBy !== 'none') {
      filtered = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case 'amount_desc':
            return parseFloat(b.amount || 0) - parseFloat(a.amount || 0);
          case 'amount_asc':
            return parseFloat(a.amount || 0) - parseFloat(b.amount || 0);
          case 'company_asc':
            return (a.company.trade_name || a.company.business_name || '').localeCompare(b.company.trade_name || b.company.business_name || '');
          case 'company_desc':
            return (b.company.trade_name || b.company.business_name || '').localeCompare(a.company.trade_name || a.company.business_name || '');
          case 'status_asc':
            return a.status.localeCompare(b.status);
          case 'status_desc':
            return b.status.localeCompare(a.status);
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [quotas, searchTerm, sortBy, statusFilter]);

  const totalPages = Math.ceil(filteredAndSortedQuotas.length / itemsPerPage);
  const paginatedQuotas = filteredAndSortedQuotas.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Resetear a la página 1 cuando cambien los filtros
  useEffect(() => {
    setPage(1);
  }, [searchTerm, sortBy, statusFilter]);

  // Resetear a la página 1 si cambian las cuotas
  useEffect(() => {
    setPage(1);
  }, [quotas]);

  // Ajustar página si las cuotas disminuyen y la página actual queda fuera de rango
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [filteredAndSortedQuotas, page, totalPages]);

  // Calcular estadísticas (basadas en las cuotas filtradas)
  const stats = useMemo(() => {
    const totalQuotas = filteredAndSortedQuotas.length;
    const pendingQuotas = filteredAndSortedQuotas.filter(quota => quota.status === 'pending').length;
    const paidQuotas = filteredAndSortedQuotas.filter(quota => quota.status === 'paid').length;
    const overdueQuotas = filteredAndSortedQuotas.filter(quota => quota.status === 'overdue').length;
    
    // Convertir strings a números para cálculos
    const totalAmount = filteredAndSortedQuotas.reduce((sum, quota) => sum + parseFloat(quota.amount || 0), 0);
    const paidAmount = filteredAndSortedQuotas
      .filter(quota => quota.status === 'paid')
      .reduce((sum, quota) => sum + parseFloat(quota.amount || 0), 0);
    
    return {
      totalQuotas,
      pendingQuotas,
      paidQuotas,
      overdueQuotas,
      totalAmount,
      paidAmount,
      totalOriginal: quotas.length // Para mostrar el total original
    };
  }, [filteredAndSortedQuotas, quotas]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin pago';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        label: 'Pendiente',
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock
      },
      paid: {
        label: 'Pagado',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle
      },
      overdue: {
        label: 'Vencido',
        color: 'bg-red-100 text-red-800',
        icon: AlertCircle
      }
    };
    return configs[status] || configs.pending;
  };

  const handleExportExcel = () => {
    onExportExcel();
  };

  // Función para limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('none');
    setStatusFilter('all');
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "bg-blue-50 border-blue-200" }) => (
    <div className={`p-4 rounded-lg border ${color}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        <Icon className="h-8 w-8 text-gray-400" />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header con información de UF */}
      <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <div>
              <h3 className="text-lg font-semibold text-green-800">
                Cuotas Confirmadas
              </h3>
              <p className="text-green-700">
                Valor UF utilizado: <span className="font-bold">${ufUsed}</span>
              </p>
            </div>
          </div>
          <button
            onClick={handleExportExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
          >
            <Download className="w-4 h-4 inline mr-2" />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Total Cuotas"
          value={stats.totalQuotas}
          subtitle={stats.totalOriginal !== stats.totalQuotas ? `de ${stats.totalOriginal} totales` : undefined}
          icon={FileText}
          color="bg-blue-50 border-blue-200"
        />
        
        <StatCard
          title="Cuotas Pendientes"
          value={stats.pendingQuotas}
          icon={Clock}
          color="bg-yellow-50 border-yellow-200"
        />
        
        <StatCard
          title="Cuotas Pagadas"
          value={stats.paidQuotas}
          icon={CheckCircle}
          color="bg-green-50 border-green-200"
        />
        
        <StatCard
          title="Monto Total"
          value={formatCurrency(stats.totalAmount)}
          subtitle={`UF: $${ufUsed}`}
          icon={DollarSign}
          color="bg-purple-50 border-purple-200"
        />
        
        <StatCard
          title="Monto Pagado"
          value={formatCurrency(stats.paidAmount)}
          subtitle={stats.totalAmount > 0 ? `${((stats.paidAmount / stats.totalAmount) * 100).toFixed(1)}% del total` : '0% del total'}
          icon={DollarSign}
          color="bg-emerald-50 border-emerald-200"
        />
        
        <StatCard
          title="Monto Pendiente"
          value={formatCurrency(stats.totalAmount - stats.paidAmount)}
          subtitle={stats.totalAmount > 0 ? `${(((stats.totalAmount - stats.paidAmount) / stats.totalAmount) * 100).toFixed(1)}% del total` : '0% del total'}
          icon={Calendar}
          color="bg-orange-50 border-orange-200"
        />
      </div>

      {/* Barra de búsqueda y filtros */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          
          {/* Buscador */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por empresa, RUT o ID de cuota..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Filtro por estado */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="paid">Pagados</option>
            </select>

            {/* Ordenamiento */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="none">Sin ordenar</option>
              <option value="amount_desc">Monto: Mayor a menor</option>
              <option value="amount_asc">Monto: Menor a mayor</option>
              <option value="company_asc">Empresa: A-Z</option>
              <option value="company_desc">Empresa: Z-A</option>
              <option value="status_asc">Estado: A-Z</option>
              <option value="status_desc">Estado: Z-A</option>
            </select>

            {(searchTerm || sortBy !== 'none' || statusFilter !== 'all') && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Información de resultados */}
        {(searchTerm || sortBy !== 'none' || statusFilter !== 'all') && (
          <div className="mt-3 text-sm text-gray-600">
            Mostrando {filteredAndSortedQuotas.length} de {quotas.length} cuotas
            {searchTerm && ` que coinciden con "${searchTerm}"`}
            {statusFilter !== 'all' && ` con estado "${getStatusConfig(statusFilter).label}"`}
          </div>
        )}
      </div>

      {/* Tabla de cuotas */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col h-full">
        
        {/* Cabecera con contador */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h4 className="text-lg font-medium text-gray-900">Detalle de Cuotas Confirmadas</h4>
          <span className="text-sm text-gray-500">
            {filteredAndSortedQuotas.length} {filteredAndSortedQuotas.length === 1 ? 'cuota' : 'cuotas'}
            {stats.totalOriginal !== stats.totalQuotas && ` de ${stats.totalOriginal} totales`}
          </span>
        </div>

        {/* Scroll interno tabla */}
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monto (CLP)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Pago
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedQuotas.length > 0 ? (
                paginatedQuotas.map((quota) => {
                  const statusConfig = getStatusConfig(quota.status);
                  const StatusIcon = statusConfig.icon;
                  const quotaAmount = parseFloat(quota.amount || 0);

                  return (
                    <tr key={quota.id} className="hover:bg-gray-100 cursor-pointer transition-colors">
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {quota.id}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap max-w-[200px] truncate" title={quota.company.trade_name || quota.company.business_name}>
                        <div className="text-sm font-medium text-gray-900">
                          {quota.company.trade_name || quota.company.business_name}
                        </div>
                        <div className="text-sm text-gray-500">{quota.company.rut}</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {quota.period} {quota.year}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(Math.round(quotaAmount))}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(quota.payment_date)}
                      </td>

                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <div className="text-3xl mb-3">
                        {searchTerm || sortBy !== 'none' || statusFilter !== 'all' ? '🔍' : '📄'}
                      </div>
                      <div className="text-sm font-medium mb-1">
                        {searchTerm || sortBy !== 'none' || statusFilter !== 'all' 
                          ? 'No se encontraron cuotas que coincidan con los filtros'
                          : 'No hay cuotas confirmadas disponibles'
                        }
                      </div>
                      {(searchTerm || sortBy !== 'none' || statusFilter !== 'all') && (
                        <button
                          onClick={clearFilters}
                          className="text-blue-600 hover:text-blue-800 text-sm mt-2"
                        >
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center mt-4 mb-4">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              siblingCount={1}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotasConfirmed;